import HapticButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { Box, CardActionArea, SxProps, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";

// Exporting from here to user other places such as on the challenge complete dialog
export const glossEffect = {
  content: '""',
  position: "absolute",
  top: 0,
  left: "-175%",
  width: "100%",
  height: "100%",
  background: "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)",
  transform: "skew(45deg)",
  zIndex: 1,
};

interface IGameCard {
  children: React.ReactNode;
  colSpan?: number;
  rowSpan?: number;
  disabled?: boolean;
  sx?: SxProps;
  onClick: () => void;
}

const GameCard = ({ children, colSpan = 1, rowSpan = 1, disabled, sx, onClick }: IGameCard) => {
  const theme = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState(0.5);

  // Increase glossy animation time based on card width to make animations more uniform
  useEffect(() => {
    const c = cardRef.current;
    if (c) {
      setTime((time) => time + c.clientWidth / 1440);
    }
  }, [cardRef]);

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`,
        cursor: "pointer",
        pointerEvents: disabled ? "none" : "auto",
        "&:hover::before": {
          opacity: "0.6",
          transform: `rotateX(7deg) translateY(-4px) scale(${(105 - rowSpan) / 100})`,
        },
      }}
    >
      <HapticButtonWrapper>
        <CardActionArea
          onClick={onClick}
          disabled={disabled}
          sx={{
            transition: `transform ${time}s cubic-bezier(0.18, 0.9, 0.58, 1)`,
            width: "100%",
            height: "100%",
            transformStyle: "preserve-3d",
            transformOrigin: "top center",
            transform: "skewX(.001deg)",
            perspective: "1000px",
            overflow: "hidden",
            backgroundColor: "dsSurface.v100",
            borderRadius: theme.spacing(0.5),
            ...sx,

            // Gloss Overlay
            "&::before": {
              ...glossEffect,
              transition: `left ${time}s cubic-bezier(0.18, 0.9, 0.58, 1)`,
            },
            "&:hover": {
              transform: "rotateX(7deg) translateY(-4px) skewX(.001deg)",
              "&::before": {
                left: "200%",
              },
            },
            "&:disabled": {
              filter: "brightness(0.5)",
              backgroundColor: "dsSurface.v100",
            },
          }}
        >
          {children}
        </CardActionArea>
      </HapticButtonWrapper>
      {disabled && (
        <Box
          sx={{
            position: "absolute",
            top: theme.spacing(2),
            right: theme.spacing(2),
          }}
        >
          <Icon icon={faLock} color={theme.palette.dsNeutral.v100} size="2x" />
        </Box>
      )}
    </Box>
  );
};

export default GameCard;
